import * as THREE from 'three';
import type { EncounterDifficulty, EnemyDefinition, EnemyState } from '../../../shared/game/world-types.js';
import { pixelsPerMeter } from '../combat/spell-channels.js';

export type BannerDifficulty = EncounterDifficulty;

/** Muted to the world's palette: moss, ochre, rust, brick and old blood rather than signal colours. */
export const DIFFICULTY_BANNER: Record<BannerDifficulty, { label: string; color: string }> = {
  easy: { label: 'Easy', color: '#94a866' },
  medium: { label: 'Medium', color: '#cfb15a' },
  hard: { label: 'Hard', color: '#cc8443' },
  deadly: { label: 'Deadly', color: '#c0533f' },
  'very-deadly': { label: 'Very Deadly', color: '#a33a3a' },
};

/** On-screen height of a banner in CSS pixels, whatever the zoom. */
const BANNER_PIXELS = 24;
/** Pixels between the highest enemy plate in the group and the banner's lower edge. */
const PLATE_CLEARANCE = 18;
const FADE_PER_SECOND = 5;
const CANVAS = { width: 384, height: 80 };

export interface BannerGroup { difficulty: BannerDifficulty; members: string[] }

/**
 * The crews that carry a banner this frame: every crew the party has assessed (the server then sends its difficulty on every
 * member) with a living member in the snapshot, so in sight, while the party is out of combat. Summons join their crew's banner.
 */
export function bannerGroups(enemies: readonly Pick<EnemyState, 'id' | 'health' | 'encounterDifficulty'>[], crews: ReadonlyMap<string, string | undefined>, inCombat: boolean): Map<string, BannerGroup> {
  const groups = new Map<string, BannerGroup>();
  if (inCombat) return groups;
  for (const enemy of enemies) {
    const encounterId = crews.get(enemy.id);
    if (!encounterId || !enemy.encounterDifficulty || enemy.health.dead) continue;
    const group = groups.get(encounterId);
    if (group) group.members.push(enemy.id); else groups.set(encounterId, { difficulty: enemy.encounterDifficulty, members: [enemy.id] });
  }
  return groups;
}

/** A dark leather ribbon with a hairline in the tier's colour and the tier's name; drawn once per difficulty. */
function drawBanner(canvas: HTMLCanvasElement, difficulty: BannerDifficulty) {
  const { label, color } = DIFFICULTY_BANNER[difficulty], context = canvas.getContext('2d')!;
  const text = label.toUpperCase(), { width, height } = canvas;
  context.clearRect(0, 0, width, height);
  context.font = `600 ${Math.round(height * .44)}px Dosis, sans-serif`;
  if ('letterSpacing' in context) context.letterSpacing = `${Math.round(height * .06)}px`;
  const ribbon = Math.min(width - 8, context.measureText(text).width + height * 1.1), left = (width - ribbon) / 2, right = left + ribbon, notch = height * .2;
  const top = height * .14, bottom = height * .86, middle = height / 2;
  context.beginPath();
  context.moveTo(left, top); context.lineTo(right, top); context.lineTo(right - notch, middle); context.lineTo(right, bottom);
  context.lineTo(left, bottom); context.lineTo(left + notch, middle); context.closePath();
  const leather = context.createLinearGradient(0, top, 0, bottom);
  leather.addColorStop(0, 'rgba(46, 37, 28, .92)'); leather.addColorStop(1, 'rgba(24, 19, 15, .92)');
  context.fillStyle = leather; context.fill();
  context.lineWidth = 2; context.strokeStyle = color; context.globalAlpha = .75; context.stroke(); context.globalAlpha = 1;
  context.textAlign = 'center'; context.textBaseline = 'middle';
  context.fillStyle = 'rgba(0, 0, 0, .8)'; context.fillText(text, width / 2, middle + 3);
  context.fillStyle = color; context.fillText(text, width / 2, middle + 1);
}

/**
 * Presentation only: a world-space banner over each assessed crew in sight, naming its difficulty. It follows the crew's centre,
 * keeps a constant size on screen, fades out when combat starts and returns over the survivors once it ends.
 */
export class EncounterBanners {
  private textures = new Map<BannerDifficulty, { texture: THREE.CanvasTexture; canvas: HTMLCanvasElement }>();
  private banners = new Map<string, { sprite: THREE.Sprite; material: THREE.SpriteMaterial; opacity: number; shown: boolean }>();
  private crews = new Map<string, string | undefined>();
  private point = new THREE.Vector3();
  private up = new THREE.Vector3();
  private projected = new THREE.Vector3();
  private disposed = false;
  constructor(private scene: THREE.Object3D) {
    // Canvas text is drawn with whatever face is loaded; redraw once the game's own has arrived.
    void globalThis.document?.fonts?.load('600 32px Dosis').then(() => { if (!this.disposed) for (const [difficulty, { canvas, texture }] of this.textures) { drawBanner(canvas, difficulty); texture.needsUpdate = true; } }, () => {});
  }

  /** Keeps the lookup in step with content, including summoned reinforcements added during play. */
  setDefinitions(enemies: readonly EnemyDefinition[]) {
    this.crews.clear();
    for (const enemy of enemies) this.crews.set(enemy.id, enemy.encounterId);
  }

  /** `heights` is each unit's plate height above its feet; `seconds` the real time since the last frame. */
  update(snapshot: { enemies: readonly Pick<EnemyState, 'id' | 'health' | 'encounterDifficulty'>[]; inCombat: boolean }, units: ReadonlyMap<string, THREE.Object3D>, heights: ReadonlyMap<string, number>,
    camera: THREE.Camera, viewportHeight: number, seconds: number) {
    const groups = bannerGroups(snapshot.enemies, this.crews, snapshot.inCombat);
    const up = this.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    for (const [id, group] of groups) {
      // Centred over the crew, and lifted on screen clear of the highest plate, so it never sits among the crew's own plates.
      let x = 0, z = 0, y = -Infinity, top = -Infinity, count = 0;
      for (const member of group.members) {
        const unit = units.get(member);
        if (!unit) continue;
        const plate = unit.position.y + (heights.get(member) ?? 2);
        x += unit.position.x; z += unit.position.z; y = Math.max(y, plate); count++;
        top = Math.max(top, this.point.set(unit.position.x, plate, unit.position.z).project(camera).y);
      }
      if (!count) continue;
      const centre = this.point.set(x / count, y, z / count), meters = 1 / Math.max(1e-3, pixelsPerMeter(camera, viewportHeight, centre));
      const below = Math.max(0, (top - this.projected.copy(centre).project(camera).y) / 2 * viewportHeight);
      const banner = this.banner(id, group.difficulty);
      banner.sprite.position.copy(centre).addScaledVector(up, (below + PLATE_CLEARANCE + BANNER_PIXELS / 2) * meters);
      banner.sprite.scale.set(BANNER_PIXELS * CANVAS.width / CANVAS.height * meters, BANNER_PIXELS * meters, 1);
      banner.shown = true;
    }
    for (const [id, banner] of this.banners) {
      if (!groups.has(id)) banner.shown = false;
      banner.opacity = THREE.MathUtils.clamp(banner.opacity + (banner.shown ? 1 : -1) * FADE_PER_SECOND * seconds, 0, 1);
      banner.material.opacity = banner.opacity; banner.sprite.visible = banner.opacity > 0;
    }
  }
  clear() { for (const banner of this.banners.values()) { banner.shown = false; banner.opacity = 0; banner.sprite.visible = false; } }
  dispose() {
    this.disposed = true;
    for (const banner of this.banners.values()) { banner.sprite.removeFromParent(); banner.material.dispose(); }
    for (const { texture } of this.textures.values()) texture.dispose();
    this.banners.clear(); this.textures.clear();
  }

  private texture(difficulty: BannerDifficulty) {
    let entry = this.textures.get(difficulty);
    if (!entry) {
      const canvas = document.createElement('canvas'); canvas.width = CANVAS.width; canvas.height = CANVAS.height;
      drawBanner(canvas, difficulty);
      const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 4;
      this.textures.set(difficulty, entry = { texture, canvas });
    }
    return entry.texture;
  }
  private banner(id: string, difficulty: BannerDifficulty) {
    let banner = this.banners.get(id);
    if (!banner) {
      // Drawn over the world without depth, untouched by fog and tone mapping, so the tier reads in its own colour.
      const material = new THREE.SpriteMaterial({ map: this.texture(difficulty), transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false, opacity: 0 });
      const sprite = new THREE.Sprite(material); sprite.renderOrder = 20; sprite.visible = false; sprite.name = `encounter-banner:${id}`;
      this.scene.add(sprite);
      this.banners.set(id, banner = { sprite, material, opacity: 0, shown: false });
    }
    return banner;
  }
}
